import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { ArrowsUpDownIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useWallet } from '../../contexts/WalletContext';
import { CHAINS, CONTRACTS, BRIDGE_CONFIG, ERROR_MESSAGES, SUCCESS_MESSAGES } from '../../config/constants';
import { contractUtils } from '../../utils/contractUtils';
import { addTransaction, trackTransaction } from '../../utils/transactionManager';
import ChainSelector from './ChainSelector';
import AmountInput from './AmountInput';
import RecipientInput from './RecipientInput';
import FeeDisplay from './FeeDisplay';
import TransactionSummary from './TransactionSummary';
import Button from '../UI/Button';
import toast from 'react-hot-toast';

const BridgeInterface = () => {
  const {
    isConnected,
    address,
    chainId,
    provider,
    signer,
    balance,
    switchChain,
    isCorrectChain,
    getChainInfo,
  } = useWallet();

  // Bridge state
  const [fromChain, setFromChain] = useState(CHAINS.RONIN.id);
  const [toChain, setToChain] = useState(CHAINS.BNB.id);
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [feeEstimate, setFeeEstimate] = useState(null);
  const [timeDelay, setTimeDelay] = useState(null);
  const [isEstimatingFee, setIsEstimatingFee] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isBridging, setIsBridging] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [bridgeStatus, setBridgeStatus] = useState(null);

  const loadBridgeStatus = useCallback(async () => {
    try {
      const status = await contractUtils.getBridgeStatus(fromChain);
      setBridgeStatus(status);
    } catch (error) {
      console.error('Failed to load bridge status:', error);
    }
  }, [fromChain]);


  const estimateFee = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) return;

    setIsEstimatingFee(true);
    try {
      const estimate = await contractUtils.calculateFee(fromChain, amount);
      setFeeEstimate(estimate);
    } catch (error) {
      console.error('Fee estimation failed:', error);
      toast.error('Failed to estimate fees');
    } finally {
      setIsEstimatingFee(false);
    }
  }, [amount, fromChain, toChain]);

  const checkTimeDelay = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) return;

    try {
      const delay = await contractUtils.getTimeDelay(fromChain, amount);
      setTimeDelay(delay);
    } catch (error) {
      console.error('Failed to get time delay:', error);
    }
  }, [amount, fromChain, toChain]);

  const checkAllowance = useCallback(async () => {
    if (!provider || !address || fromChain !== chainId) return;

    try {
      const tokenAddress = fromChain === CHAINS.RONIN.id 
        ? CONTRACTS.RONIN.PFVS_TOKEN 
        : CONTRACTS.BNB.PFVS_TOKEN;
      
      const bridgeAddress = fromChain === CHAINS.RONIN.id 
        ? CONTRACTS.RONIN.BRIDGE 
        : CONTRACTS.BNB.BRIDGE;

      if (!tokenAddress || !bridgeAddress || 
          tokenAddress === '0x0000000000000000000000000000000000000000' ||
          bridgeAddress === '0x0000000000000000000000000000000000000000') {
        return;
      }

      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function allowance(address owner, address spender) view returns (uint256)'],
        provider
      );

      const currentAllowance = await tokenContract.allowance(address, bridgeAddress);

      const amountBN = ethers.utils.parseEther(amount || '0');
      setNeedsApproval(currentAllowance.lt(amountBN));

    } catch (error) {
      console.error('Failed to check allowance:', error);
    }
  }, [provider, address, fromChain, chainId, amount]);

  const handleChainSwap = () => {
    const newFromChain = toChain;
    const newToChain = fromChain;
    setFromChain(newFromChain);
    setToChain(newToChain);
    
    // Auto-switch to the new source chain if connected
    if (isConnected && chainId !== newFromChain) {
      switchChain(newFromChain);
    }
  };

  const handleMaxClick = () => {
    if (balance.pfvs) {
      setAmount(balance.pfvs);
    }
  };

  const validateTransaction = () => {
    if (!amount || parseFloat(amount) <= 0) {
      throw new Error(ERROR_MESSAGES.INVALID_AMOUNT);
    }

    const amountNum = parseFloat(amount);
    const minAmount = parseFloat(BRIDGE_CONFIG.MIN_AMOUNT);
    const maxAmount = parseFloat(BRIDGE_CONFIG.MAX_AMOUNT);

    if (amountNum < minAmount) {
      throw new Error(`Minimum amount is ${minAmount} PFVS`);
    }

    if (amountNum > maxAmount) {
      throw new Error(`Maximum amount is ${maxAmount} PFVS`);
    }

    if (!balance.pfvs || parseFloat(balance.pfvs || '0') < amountNum) {
      throw new Error(ERROR_MESSAGES.INSUFFICIENT_BALANCE);
    }


    // Validate recipient address if provided
    if (recipient && !ethers.utils.isAddress(recipient)) {
      throw new Error('Invalid recipient address format');
    }
  };

  const handleApprove = async () => {
    if (!signer || !amount) return;

    setIsApproving(true);
    try {
      validateTransaction();

      if (fromChain !== chainId) {
        await switchChain(fromChain);
        return;
      }

      const tokenAddress = fromChain === CHAINS.RONIN.id 
        ? CONTRACTS.RONIN.PFVS_TOKEN 
        : CONTRACTS.BNB.PFVS_TOKEN;
      
      const bridgeAddress = fromChain === CHAINS.RONIN.id 
        ? CONTRACTS.RONIN.BRIDGE 
        : CONTRACTS.BNB.BRIDGE;

      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function approve(address spender, uint256 amount) returns (bool)'],
        signer
      );

      const amountToApprove = ethers.utils.parseEther(amount);
      const tx = await tokenContract.approve(bridgeAddress, amountToApprove);

      toast.success('Approval transaction submitted');
      
      await tx.wait();
      toast.success(SUCCESS_MESSAGES.APPROVAL_SUCCESS);
      
      // Refresh allowance
      await checkAllowance();

    } catch (error) {
      console.error('Approval failed:', error);
      toast.error(error.message || 'Approval failed');
    } finally {
      setIsApproving(false);
    }
  };

  const handleBridge = async () => {
    if (!signer || !amount) return;

    setIsBridging(true);
    try {
      validateTransaction();

      if (fromChain !== chainId) {
        await switchChain(fromChain);
        return;
      }

      // Check if bridge is paused
      const pauseStatus = await contractUtils.getBridgePauseStatus(fromChain);
      if (pauseStatus.isPaused) {
        throw new Error('Bridge is currently paused for maintenance');
      }

      const bridgeAddress = fromChain === CHAINS.RONIN.id 
        ? CONTRACTS.RONIN.BRIDGE 
        : CONTRACTS.BNB.BRIDGE;

      const amountBN = ethers.utils.parseEther(amount);
      
      // PuffBridge uses unified lockOrBurn function for both chains
      const bridgeContract = new ethers.Contract(
        bridgeAddress,
        ['function lockOrBurn(uint256 amount, address receiver) payable'],
        signer
      );

      const feeInWei = feeEstimate ? ethers.utils.parseEther(feeEstimate.totalFeeEth) : '0';
      
      const recipientAddress = recipient || address;
      const tx = await bridgeContract.lockOrBurn(
        amountBN,
        recipientAddress,
        { value: feeInWei }
      );

      toast.success(SUCCESS_MESSAGES.TRANSACTION_SUBMITTED);

      // Add transaction to local tracking (no server API)
      addTransaction({
        txHash: tx.hash,
        fromChain,
        toChain,
        amount,
        recipient: recipient || address,
        sender: address,
        feeEstimate,
        timeDelay: timeDelay ? timeDelay.delaySeconds : 0
      });

      // Start tracking transaction status via blockchain events
      trackTransaction(tx.hash, fromChain, toChain, provider);

      // Show transaction tracking info
      toast.success(`Transaction submitted! Hash: ${tx.hash.substring(0, 10)}...`, {
        duration: 8000,
        onClick: () => {
          const explorerUrl = fromChain === CHAINS.RONIN.id 
            ? `${CHAINS.RONIN.explorerUrl}/tx/${tx.hash}`
            : `${CHAINS.BNB.explorerUrl}/tx/${tx.hash}`;
          window.open(explorerUrl, '_blank');
        }
      });

      // Reset form
      setAmount('');
      setRecipient('');
      setFeeEstimate(null);
      setTimeDelay(null);

      // Refresh balances

    } catch (error) {
      console.error('Bridge transaction failed:', error);
      toast.error(error.message || 'Bridge transaction failed');
    } finally {
      setIsBridging(false);
    }
  };

  const isFormValid = () => {
    return (
      isConnected &&
      amount &&
      parseFloat(amount) > 0 &&
      feeEstimate &&
      !isEstimatingFee &&
      isCorrectChain(fromChain)
    );
  };

  const getButtonText = () => {
    if (!isConnected) return 'Connect Wallet';
    if (!isCorrectChain(fromChain)) return `Switch to ${getChainInfo(fromChain)?.name}`;
    if (needsApproval && !isApproving) return 'Approve PFVS';
    if (isApproving) return 'Approving...';
    if (isBridging) return 'Bridging...';
    return 'Bridge Tokens';
  };

  const getButtonAction = () => {
    if (!isConnected) return null;
    if (!isCorrectChain(fromChain)) return () => switchChain(fromChain);
    if (needsApproval && !isApproving) return handleApprove;
    return handleBridge;
  };

  // Load initial data
  useEffect(() => {
    loadBridgeStatus();
  }, [loadBridgeStatus]);

  // Update fee estimate when parameters change
  useEffect(() => {
    if (amount && parseFloat(amount) > 0) {
      estimateFee();
      checkTimeDelay();
    } else {
      setFeeEstimate(null);
      setTimeDelay(null);
    }
  }, [amount, fromChain, toChain, estimateFee, checkTimeDelay]);

  // Check token allowance when chain or amount changes
  useEffect(() => {
    if (isConnected && amount && parseFloat(amount) > 0) {
      checkAllowance();
    }
  }, [isConnected, amount, fromChain, chainId, checkAllowance]);

  // Auto-switch to source chain when fromChain changes
  useEffect(() => {
    if (isConnected && chainId !== fromChain) {
      // Add a small delay to avoid immediate wallet popups on page load
      const timer = setTimeout(() => {
        switchChain(fromChain);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isConnected, fromChain, chainId, switchChain]);

  if (bridgeStatus?.isPaused && bridgeStatus?.pauseReason) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="text-warning-500 mb-4">
          <InformationCircleIcon className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Bridge Temporarily Paused</h3>
        <p className="text-slate-600 dark:text-slate-400">
          {bridgeStatus.pauseReason}
        </p>
      </div>
    );
  }

  return (
    <div className="glass-card p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gradient mb-2">PFVS Bridge</h2>
        <p className="text-slate-600 dark:text-slate-400">
          Transfer PFVS tokens between Ronin and BNB Smart Chain
        </p>
      </div>

      {/* Chain Selection */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <ChainSelector
            selectedChain={fromChain}
            onChainSelect={setFromChain}
            label="From"
            excludeChain={toChain}
            isSource={true}
            isWrongNetwork={isConnected && !isCorrectChain(fromChain)}
          />
          
          <button
            onClick={handleChainSwap}
            className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
                              <ArrowsUpDownIcon className="w-5 h-5" />
          </button>
          
          <ChainSelector
            selectedChain={toChain}
            onChainSelect={setToChain}
            label="To"
            excludeChain={fromChain}
          />
        </div>

        {/* Network Status - show simple status instead of manual switch */}
        {isConnected && !isCorrectChain(fromChain) && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center gap-3">
              <InformationCircleIcon className="w-5 h-5 text-blue-500" />
              <div className="text-sm">
                <p className="text-blue-800 dark:text-blue-200">
                  Switching to {getChainInfo(fromChain)?.name}...
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Amount Input */}
      <AmountInput
        amount={amount}
        onAmountChange={setAmount}
        balance={balance.pfvs}
        onMaxClick={handleMaxClick}
        disabled={!isConnected || !isCorrectChain(fromChain)}
        isWrongNetwork={isConnected && !isCorrectChain(fromChain)}
        sourceChainName={getChainInfo(fromChain)?.name}
      />

      {/* Recipient Input */}
      <RecipientInput
        recipient={recipient}
        onRecipientChange={setRecipient}
        currentAddress={address}
        disabled={!isConnected || !isCorrectChain(fromChain)}
        toChainName={getChainInfo(toChain)?.name}
      />

      {/* Fee Display */}
      {(feeEstimate || isEstimatingFee) && (
        <FeeDisplay
          feeEstimate={feeEstimate}
          isLoading={isEstimatingFee}
          timeDelay={timeDelay}
        />
      )}

      {/* Transaction Summary */}
      {feeEstimate && amount && (
        <TransactionSummary
          fromChain={fromChain}
          toChain={toChain}
          amount={amount}
          recipient={recipient || address}
          feeEstimate={feeEstimate}
          timeDelay={timeDelay}
        />
      )}


      {/* Action Button */}
      <Button
        onClick={getButtonAction()}
        disabled={!isFormValid() || isApproving || isBridging}
        loading={isApproving || isBridging}
        variant="primary"
        size="lg"
        className="w-full"
      >
        {getButtonText()}
      </Button>

      {/* Warnings */}
      {timeDelay && timeDelay.delaySeconds > 0 && (
        <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <InformationCircleIcon className="w-5 h-5 text-warning-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-warning-800 dark:text-warning-200">
                Time Delay Applied
              </p>
              <p className="text-warning-700 dark:text-warning-300 mt-1">
                Large amounts require a {timeDelay.delayHours} hour security delay before completion.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BridgeInterface; 